import React from "react";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Container,
  Divider,  
  Link as MuiLink,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LaunchIcon from "@mui/icons-material/Launch";
import { Link as RouterLink } from "react-router-dom";

const Products = () => {
  return (
    <Box component="main" sx={{ py: { xs: 4, md: 6 } }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 5 } }}>
        <Typography variant="h4" component="h1" fontWeight={800} gutterBottom>
          Bhamu
          <Box
            component="span"
            sx={{
              color: "error.main",
              fontWeight: 900,
              fontSize: "1.15em",   // slightly bigger than surrounding text
              lineHeight: 1,
              display: "inline-block",
            }}
          >
            i
          </Box>
          ntelli Systems Products
        </Typography>


          <Typography variant="subtitle1" color="text.secondary">
            Practical, cloud-native products built by BhamuIntelli Systems LLP for education,
            productivity, and digital safety.
          </Typography>
        </Box>

        <Grid container spacing={3} alignItems="stretch">
          {/* StudyTunnel */}
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                  <Typography variant="h5" component="h2" fontWeight={700}>
                    StudyTunnel
                  </Typography>
                  <Chip label="Flagship" size="small" />
                </Stack>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                  An AI-powered online learning and assessment platform to help learners improve through
                  structured practice and actionable insights.
                </Typography>

                <List dense sx={{ mt: 1.5 }}>
                  {[
                    "Online exams and practice-based learning flows",
                    "Insights to identify weak areas and track progress",
                    "Job-oriented test series and preparation paths",
                  ].map((t) => (
                    <ListItem key={t} disableGutters sx={{ py: 0.25 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircleOutlineIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primaryTypographyProps={{ variant: "body2" }} primary={t} />
                    </ListItem>
                  ))}
                </List>

                <Divider sx={{ my: 2 }} />

                {/* Solutions sub-note */}
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: "action.hover",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    Solutions on StudyTunnel
                  </Typography>

                  <Stack spacing={0.75}>
                    <MuiLink
                      href="https://studytunnel.com/student"
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                    >
                      Students <LaunchIcon fontSize="inherit" />
                    </MuiLink>

                    <MuiLink
                      href="https://studytunnel.com/teacher"
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                    >
                      Teachers <LaunchIcon fontSize="inherit" />
                    </MuiLink>

                    <MuiLink
                      href="https://studytunnel.com/parent"
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                    >
                      Parents <LaunchIcon fontSize="inherit" />
                    </MuiLink>
                  </Stack>
                </Box>
              </CardContent>

              <CardActions sx={{ px: 2, pb: 2, pt: 0, gap: 1, flexWrap: "wrap" }}>
                {/* Retain main homepage link */}
                <Button
                  variant="contained"
                  component="a"
                  href="https://studytunnel.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<LaunchIcon />}
                >
                  Visit StudyTunnel
                </Button>

                <Button
                  variant="outlined"
                  component="a"
                  href="https://studytunnel.com/test-series-jobs"
                  target="_blank"
                  rel="noopener noreferrer"
                  
                >
                  Exams
                </Button>
              </CardActions>
            </Card>
          </Grid>

          {/* Child Monitoring App */}
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                  <Typography variant="h5" component="h2" fontWeight={700}>
                    Child Monitoring App
                  </Typography>
                  <Chip label="Safety" size="small" />
                </Stack>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                  A privacy-first parental support app that helps families understand screen usage and
                  promote healthy digital habits.
                </Typography>

                <List dense sx={{ mt: 1.5 }}>
                  {[
                    "Screen time and app usage visibility",
                    "Optional location awareness with consent",
                    "Safety alerts and activity summaries",
                  ].map((t) => (
                    <ListItem key={t} disableGutters sx={{ py: 0.25 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircleOutlineIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primaryTypographyProps={{ variant: "body2" }} primary={t} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>

              <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                <Button variant="contained" component={RouterLink} to="/contact-us">
                  Request details
                </Button>
              </CardActions>
            </Card>
          </Grid>

          {/* AI Assistant */}
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                  <Typography variant="h5" component="h2" fontWeight={700}>
                    AI Assistant
                  </Typography>
                  <Chip label="Productivity" size="small" />
                </Stack>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                  An AI-powered assistant that helps teams automate repetitive tasks, search internal
                  knowledge, and improve operational efficiency.
                </Typography>

                <List dense sx={{ mt: 1.5 }}>
                  {[
                    "Knowledge base Q&A and internal search",
                    "Summaries, suggested responses, and workflow automation",
                    "API-ready integrations (webhooks and modern services)",
                  ].map((t) => (
                    <ListItem key={t} disableGutters sx={{ py: 0.25 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircleOutlineIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primaryTypographyProps={{ variant: "body2" }} primary={t} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>

              <CardActions sx={{ px: 2, pb: 2, pt: 0, gap: 1, flexWrap: "wrap" }}>
                <Button variant="contained" component={RouterLink} to="/contact-us">
                  Talk to us
                </Button>
                <Button variant="outlined" component={RouterLink} to="/technologies">
                  Our tech stack
                </Button>
              </CardActions>
            </Card>
          </Grid>
        </Grid>

        {/* Bottom CTA */}
        <Box
          sx={{
            mt: { xs: 4, md: 6 },
            p: { xs: 2.5, md: 3.5 },
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            textAlign: "center",
          }}
        >
          <Typography variant="h5" component="h2" fontWeight={800} gutterBottom>
            Need a custom product?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We design and build scalable, cloud-native products using modern web technologies.
          </Typography>
          <Button variant="contained" component={RouterLink} to="/contact-us">
            Contact Us
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default Products;
